import {getMessaging, getToken, onMessage} from "firebase/messaging";
import Bowser from "bowser";
import {getApp, getApps, initializeApp} from "firebase/app";

(function() {
    "use strict";

    angular
        .module("vrm.core")
        .service("NotificationsService", NotificationsService)
        .constant("NOTIFICATION_CHANNELS", {
            email: "1",
            desktop: "2",
            mobile: "3",
            whatsapp: "4"
        })
        .constant("NOTIF_LS_KEYS", {
            FCM_TOKEN: "mobile-fcm-token",
            APN_TOKEN: "mobile-apn-token",
            DEVICE_NAME: "mobile-device-name"
        })
        .constant("TOKEN_TYPE", {
            FCM: "fcm",
            APNS: "apns"
        });

    /* @ngInject */
    function NotificationsService(
        locale,
        $log,
        $rootScope,
        VrmModalService,
        ApiService,
        vrmNotificationsService,
        vrmNotificationType,
        NOTIFICATION_CHANNELS,
        NOTIF_LS_KEYS,
        TOKEN_TYPE,
        vrmConfig
    ) {
        var service = {
            initFirebaseMessaging: initFirebaseMessaging,
            processPushNotificationsRequest: processPushNotificationsRequest,
            areNotificationsSupported: areNotificationsSupported,
            handleSubscriptionToChannel: handleSubscriptionToChannel,
            unsubscribeDeviceFromChannel: unsubscribeDeviceFromChannel,
            checkMobileWrapper: checkMobileWrapper,
            askPermission: askPermission,
            activateDesktopNotifications: activateDesktopNotifications,
            getBrowserName: getBrowserName,
            getNotificationChannels: getNotificationChannels,
            getDeviceToken: getDeviceToken,
            getDeviceName: getDeviceName,
            getTokenType: getTokenType,

            deviceName: "Unknown device"
        };

        var firebaseMessaging = null;

        function initFirebaseMessaging() {
            const firebaseApp =
                getApps().length === 0
                    ? initializeApp({
                        apiKey: vrmConfig.firebase.apiKey,
                        authDomain: vrmConfig.firebase.authDomain,
                        projectId: vrmConfig.firebase.projectId,
                        storageBucket: vrmConfig.firebase.storageBucket,
                        messagingSenderId: vrmConfig.firebase.messagingSenderId,
                        appId: vrmConfig.firebase.appId,
                        measurementId: vrmConfig.firebase.measurementId
                    })
                    : getApp();

            firebaseMessaging = getMessaging(firebaseApp);

            // handle messages when tab is active
            onMessage(firebaseMessaging, (payload) => {
                handleFirebaseOnMessage(payload);
            });

        }

        const handleFirebaseOnMessage = payload => {
            console.log("[firebase-messaging] Received foreground message.", payload);
            const supported = "Notification" in window && "serviceWorker" in navigator;
            if (!supported) {
                return;
            }

            const notificationTitle = payload.data.twi_title;
            const notificationOptions = {
                body: payload.data.twi_body,
                icon: "/img/vrm-browser-notification-icon.png",
                badge: "/img/vrm-browser-notification-icon.png",
                data: { idSite: payload.data.idSite }
            };
            const notif = new Notification(notificationTitle, notificationOptions);
            notif.addEventListener("click", function (event) {
                if (event.target.data && event.target.data.idSite) {
                    event.preventDefault();
                    window.open(
                        `/installation/${event.target.data.idSite}/alarmlogs`,
                        "_blank"
                    );
                }
            });
        };

        function getNotificationChannels(idUser, params = null) {
            return ApiService.call(
                "/users/" + idUser + "/notification-channels",
                params,
                "GET"
            );
        }

        function checkMobileWrapper() {
            let mobileToken =
                window.localStorage.getItem("mobile-fcm-token") ||
                window.localStorage.getItem("mobile-apn-token");
            return mobileToken !== null;
        }

        function getBrowserName() {
            const browser = Bowser.getParser(window.navigator.userAgent);
            return `${browser.getOSName()} - ${browser.getBrowserName()} v${browser.getBrowserVersion()}`;
        }

        function processPushNotificationsRequest(showErrorModal = true) {
            return new Promise((resolve, reject) => {
                return requestNotificationsPermission(showErrorModal)
                    .then(function() {
                        generateTwilioToken()
                            .then(function(token) {
                                resolve(token);
                            })
                            .catch(function(err) {
                                reject(err);
                            });
                    })
                    .catch(function(err) {
                        if (err) $log.error(err);
                        reject(err);
                    });
            });
        }

        function requestNotificationsPermission(showErrorModal) {
            return new Promise(function(resolve, reject) {
                if (!areNotificationsSupported()) {
                    if (showErrorModal)
                        VrmModalService.openErrorModal(
                            $rootScope,
                            locale.getString(
                                "notifications.browser_notifications_not_supported_message"
                            ),
                        );
                    reject(
                        locale.getString(
                            "notifications.browser_notifications_not_supported_message"
                        )
                    );
                }

                Notification.requestPermission().then((permission) => {
                    if (permission === "granted") {
                        resolve();
                    } else {
                        if (showErrorModal) {
                            VrmModalService.openErrorModal(
                                $rootScope,
                                locale.getString("notifications.browser_notifications_error_message"),
                            );
                        }

                        reject(locale.getString("notifications.browser_notifications_error_message"));
                    }
                })
                .catch(() => {
                    if (showErrorModal) {
                        VrmModalService.openErrorModal(
                            $rootScope,
                            locale.getString("notifications.browser_notifications_error_message"),
                        );
                    }

                    reject(locale.getString("notifications.browser_notifications_error_message"));
                });
             });
        }

        function getTokenType() {
            const fcmToken = window.localStorage.getItem(NOTIF_LS_KEYS.FCM_TOKEN);
            if (fcmToken) {
                return TOKEN_TYPE.FCM;
            }

            const apnsToken = window.localStorage.getItem(NOTIF_LS_KEYS.APN_TOKEN);
            if (apnsToken) {
                return TOKEN_TYPE.APNS;
            }

            // return fcm by default if there is any delay in saving the token to local storage
            return TOKEN_TYPE.FCM;
        }

        async function getDeviceToken() {
            if (checkMobileWrapper()) {
                return (
                    window.localStorage.getItem(NOTIF_LS_KEYS.FCM_TOKEN) ||
                    window.localStorage.getItem(NOTIF_LS_KEYS.APN_TOKEN)
                );
            }
            try {
                return await generateTwilioToken();
            } catch (err) {
                return null;
            }
        }

        function getDeviceName() {
            if (checkMobileWrapper()) {
                return window.localStorage.getItem(NOTIF_LS_KEYS.DEVICE_NAME);
            }
            return getBrowserName();
        }

        function generateTwilioToken() {
            return new Promise(function(resolve, reject) {
                // put into the storage by the wrapper
                getToken(firebaseMessaging)
                    .then(function(currentToken) {
                        if (currentToken) {
                            resolve(currentToken);
                        } else {
                            $log.warn(
                                "No Instance ID token available. Request permission to generate one."
                            );
                            reject(
                                "No Instance ID token available. Request permission to generate one."
                            );
                        }
                    })
                    .catch(function(err) {
                        reject(err);
                    });
            });
        }

        function areNotificationsSupported() {
            return "Notification" in window || checkMobileWrapper();
        }

        function handleSubscriptionToChannel(channel, idUser, meta = {}) {
            return new Promise(function(resolve, reject) {
                const data = {
                    idNotificationChannel: channel,
                    meta
                };

                ApiService.call(
                    "/users/" + idUser + "/notification-channels",
                    data,
                    "POST"
                )
                    .then(function(res) {
                        resolve(res);
                    })
                    .catch(function(err) {
                        reject(err);
                    });
            });
        }

        function unsubscribeDeviceFromChannel(idUser, idBinding) {
            console.log({ idUser, idBinding})
            return ApiService.call(
                "/users/" + idUser + "/notification-channels/?idBinding=" + idBinding,
                { },
                "DELETE",
                false,
                "json",
                true
            );
        }

        function askPermission() {
            locale.ready("notifications").then(function() {
                vrmNotificationsService.add({
                    type: vrmNotificationType.ERROR,
                    message: locale.getString(
                        "notifications.mobile_push_notifications_description_os_disabled"
                    )
                });
            });
        }

        function activateDesktopNotifications(userId) {
            return processPushNotificationsRequest()
                .then(function(deviceToken) {
                    return handleSubscriptionToChannel(
                        NOTIFICATION_CHANNELS.desktop,
                        userId,
                        {
                            deviceToken,
                            deviceName: getBrowserName(),
                            tokenType: TOKEN_TYPE.FCM,
                        }
                    ).catch(function(err) {
                        $log.error(err);
                    });
                })
                .catch(function(error) {
                    $log.error(error);
                    locale.ready("notifications").then(function() {
                        vrmNotificationsService.add({
                            type: vrmNotificationType.ERROR,
                            message: locale.getString(
                                "notifications.browser_notifications_not_supported_message"
                            )
                        });
                    });
                });
        }

        return service;
    }
})();
